import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  MapContainer,
  TileLayer,
  useMapEvent
} from 'react-leaflet'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  useNavigate,
  useSearchParams
} from 'react-router-dom'
// eslint-disable-next-line import/no-extraneous-dependencies
import MarkerClusterGroup from '@changey/react-leaflet-markercluster'
import Leaflet, { latLng } from 'leaflet'
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  downloadTile,
  saveTile,
  savetiles,
  tileLayerOffline
} from 'leaflet.offline'
import {
  authSelectors,
  deleteProfileItem,
  getNotificationMessage,
  handleLocationMyPosition,
  markersSelectors,
  setAddFriendStatus,
  setDeleteFriendStatus,
  setGoToMinZoomOut,
  setMarkerUpdate,
  setPosts,
  setProfileItemId,
  setRemoveFriendRequestStatus,
  setSelectedMarkerData,
  showCurrentChatFriendMarkerCoordinate,
  usersSelectors,
  setSelectedEventData,
  setEventId,
  setEventMarkerUpdate,
  addNaturalWidths
} from 'store'
import { chatCredentialsSelectors } from 'store/chat/selectors'
import {
  INITIAL_POSITION,
  LOCATION_TYPE,
  MAP_ATTRIBUTION,
  MAP_URL,
  MIN_ZOOM_LEVEL,
  REJECTED,
  UNSUPPORTED_MAX_ZOOM_LEVEL,
  ZOOM_LEVEL,
  SUCCEEDED
} from 'utils/constants'
import {
  isAuth,
  isLiteralNumber,
  flyLocation,
  hideCounter,
  showCounter
} from 'utils/helpers'

import { EventMarkerPopup } from './Markers/EventMarkerPopup'
import { MarkerPopup } from './Markers/MarkerPopup'
import { Markers } from './Markers'
import { StyledMapContainer } from './styled'

import 'leaflet/dist/leaflet.css'

export const Map = ({
  mapRef,
  setVisibleMarkersCount,
  setShowMarkerCount
}) => {
  const { xmppLiveLocation } = useSelector(chatCredentialsSelectors)
  const {
    currentChatFriendMarkerCoordinate,
    filters,
    getMarkersStatus,
    goToMinZoomOut,
    isLocationMyPosition,
    markers,
    markerUpdate,
    selectedMarkerData,
    eventMarkers,
    selectedEventData,
    eventMarkerUpdate
  } = useSelector(markersSelectors)
  const {
    activeProfile,
    getUserProfileStatus,
    openPopupBlogs,
    openPopupEvents,
    selectedUserProfile,
    user,
    userProfile
  } = useSelector(usersSelectors)
  const { signInStatus } = useSelector(authSelectors)

  const [popupComponent, setPopupComponent] = useState('')
  const [leafletOfflineInitialized, setLeafletOfflineInitialized] = useState(false)
  const dispatch = useDispatch()
  const moveTimeout = useRef(null)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const lat = searchParams.get('lat')
  const lng = searchParams.get('lng')
  const zoom = searchParams.get('zoom')
  const liveLocCoord = [xmppLiveLocation?.lat, xmppLiveLocation?.lng]
  const markersRef = useRef(markers)
  const userProfileRef = useRef(userProfile)

  // eslint-disable-next-line max-len
  const locationType = activeProfile && activeProfile.location_type && Object.keys(activeProfile?.location_type)[0]
  const isLiveLocation = locationType === LOCATION_TYPE.LiveLocation

  const staticLocationPosition = activeProfile?.coordinates ?? [localStorage.getItem('userLatLS'), localStorage.getItem('userLngLS')]
  const isLocationValid = () => !Number.isNaN(xmppLiveLocation.lat)
    && !Number.isNaN(xmppLiveLocation.lng)

  useEffect(() => {
    if (
      user
      && isLocationMyPosition
      && isLiveLocation
      && isLocationValid()
      && selectedUserProfile?.id === activeProfile?.id
    ) {
      flyLocation(mapRef, xmppLiveLocation.lat, xmppLiveLocation.lng, false, false)
      dispatch(handleLocationMyPosition())
    }

    if (
      activeProfile
      && activeProfile?.coordinates?.length > 0
      && selectedUserProfile?.id === activeProfile?.id
    ) {
      flyLocation(mapRef, activeProfile.coordinates[0], activeProfile.coordinates[1])
    } else if (
      selectedUserProfile?.id !== activeProfile?.id
      && selectedUserProfile?.coordinates?.length > 0
    ) {
      flyLocation(mapRef, selectedUserProfile.coordinates[0], selectedUserProfile.coordinates[1])
    }
  }, [isLocationMyPosition])

  useEffect(() => {
    if (currentChatFriendMarkerCoordinate) {
      const {
        coord,
        isStatic,
        teleport
      } = currentChatFriendMarkerCoordinate

      flyLocation(
        mapRef,
        coord[0],
        coord[1],
        true,
        isStatic,
        teleport
      )
      dispatch(showCurrentChatFriendMarkerCoordinate(null))
    }
  }, [currentChatFriendMarkerCoordinate?.coord])

  useEffect(() => {
    if (goToMinZoomOut) {
      const mapCenter = mapRef.current.getCenter()

      flyLocation(
        mapRef,
        mapCenter.lat,
        mapCenter.lng,
        false,
        true,
        true,
        MIN_ZOOM_LEVEL
      )

      dispatch(setGoToMinZoomOut(false))
    }
  }, [goToMinZoomOut])

  const handleVisibleMarkerCount = (delay = 400) => {
    setTimeout(() => {
      let totalMarkers = markersRef.current
      let ownMarkers = userProfileRef.current
      let totalFilteredMarkers = []
      let visibleZoom = mapRef.current?.getZoom()
      let isFiltered = localStorage.getItem('is-filtered')
      let tempFilter = {
        affinities: localStorage.getItem('search-affinities').split(',').filter((x) => x !== ''),
        codewords: localStorage.getItem('search-codewords').split(',').filter((x) => x !== '')
      }

      // for filtered global markers
      if (
        isFiltered === 'true'
        && totalFilteredMarkers.length === 0
        && (
          tempFilter?.affinities?.length > 0
          || tempFilter?.codewords?.length > 0
        )
      ) {
        // Filtered Markers for others profiles
        totalMarkers?.map((e) => {
          if (
            tempFilter?.affinities.every((r) => e.affinities?.includes(r))
            && tempFilter?.codewords.every((r) => e.codewords?.includes(r))
          ) {
            totalFilteredMarkers = [...totalFilteredMarkers, e]
          }
          return null
        })

        // Filtered Markers for own profiles
        ownMarkers?.map((e) => {
          let count1 = 0
          let count2 = 0
          tempFilter?.affinities.forEach((r) => {
            for (let i = 0; i < e.affinities?.length; i += 1) {
              if (e.affinities[i].title === r) {
                count1 += 1
              }
            }
          })

          tempFilter?.codewords.forEach((r) => {
            for (let i = 0; i < e.code_words?.length; i += 1) {
              if (e.code_words[i].title === r) {
                count2 += 1
              }
            }
          })

          if (
            count1 === tempFilter?.affinities?.length
            && count2 === tempFilter?.codewords?.length
          ) {
            totalFilteredMarkers = [...totalFilteredMarkers, e]
          }
          return null
        })
      }

      ownMarkers?.map((e) => {
        if (e?.coordinates) {
          totalMarkers = [...totalMarkers, e]
          return totalMarkers
        }
        return null
      })

      if (mapRef.current) {
        if (
          visibleZoom === 4
          && isFiltered === 'false'
        ) {
          setVisibleMarkersCount(totalMarkers)
        } else if (
          visibleZoom === 4 && isFiltered === 'true'
          && (
            tempFilter?.affinities.length > 0
            || tempFilter?.codewords.length > 0
          )
        ) {
          setVisibleMarkersCount(totalFilteredMarkers)
        } else if (visibleZoom > 4) {
          let clusters = []

          /* eslint-disable no-underscore-dangle */
          mapRef.current.eachLayer((l) => {
            if (
              l instanceof Leaflet.Marker
              && mapRef.current.getBounds().contains(l.getLatLng())
              && l._group?.options.className !== 'marker-cluster-custom'
              && l.options.className !== 'event-marker'
            ) {
              clusters.push(l)
            } else if (
              l._group?.options.className === 'marker-cluster-custom'
              && l.getChildCount?.() !== 'undefined'
              && mapRef.current.getBounds().contains(l.getLatLng())
            ) {
              l.getAllChildMarkers().forEach((childMarker) => {
                if (childMarker?.options?.className !== 'event-marker') {
                  clusters.push([])
                }
              })
            }
          })
          /* eslint-disable no-underscore-dangle */

          setVisibleMarkersCount(clusters)
        }
      }
    }, delay)
  }

  const handleCoordinates = () => {
    const mapCenter = mapRef.current.getCenter()
    const mapZoom = mapRef.current.getZoom()

    if (
      !window.location.search.includes('?id')
      && !window.location.search.includes('?eventid')
    ) {
      setSearchParams({
        lat: mapCenter.lat.toFixed(6),
        lng: mapCenter.lng.toFixed(6),
        zoom: mapZoom
      })
    }

    localStorage.setItem('lat', mapCenter.lat.toFixed(6))
    localStorage.setItem('lng', mapCenter.lng.toFixed(6))
    localStorage.setItem('zoom', mapZoom)
  }

  const handleMapMoveEnd = () => {
    clearTimeout(moveTimeout.current)
    const openProfileAfterAnimation = sessionStorage.getItem('openProfileAfterAnimation')
    const openEventAfterAnimation = sessionStorage.getItem('openEventAfterAnimation')

    if (JSON.parse(openProfileAfterAnimation) > 0) {
      setTimeout(() => {
        navigate(`/?id=${openProfileAfterAnimation}`)
        sessionStorage.removeItem('openProfileAfterAnimation')
      }, 500)
    } else if (JSON.parse(openEventAfterAnimation) > 0) {
      setTimeout(() => {
        navigate(`/?eventid=${openEventAfterAnimation}`)
        sessionStorage.removeItem('openEventAfterAnimation')
      }, 500)
    } else {
      moveTimeout.current = setTimeout(() => {
        if (
          !JSON.parse(sessionStorage.getItem('isSharingBlog'))
          && !JSON.parse(sessionStorage.getItem('isChatPanelOpen'))
          && !JSON.parse(sessionStorage.getItem('id'))
          && !JSON.parse(sessionStorage.getItem('post-id'))
          && !JSON.parse(sessionStorage.getItem('eventid'))
        ) {
          handleCoordinates()
          handleVisibleMarkerCount()
        }
      }, 500)
    }
  }

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.off('moveend', handleMapMoveEnd)
      mapRef.current.on('moveend', handleMapMoveEnd)
    }
  }, [markers])

  useEffect(() => {
    markersRef.current = markers
  }, [markers])

  useEffect(() => {
    userProfileRef.current = userProfile
  }, [userProfile])

  useEffect(() => {
    if (
      markerUpdate
      || eventMarkerUpdate
      || filters
    ) {
      dispatch(setMarkerUpdate(false))
      dispatch(setEventMarkerUpdate(false))
      localStorage.setItem('search-affinities', filters.affinities)
      localStorage.setItem('search-codewords', filters.codewords)
      handleVisibleMarkerCount()
    }
  }, [markerUpdate, eventMarkerUpdate, filters])

  const handleInitialCenter = () => {
    const latLS = localStorage.getItem('lat')
    const lngLS = localStorage.getItem('lng')
    const zoomLS = localStorage.getItem('zoom')
    const activeProfileLat = localStorage.getItem('activeProfileLat')
    const activeProfileLng = localStorage.getItem('activeProfileLng')
    let centerCoordinates = staticLocationPosition ?? INITIAL_POSITION

    if (
      !window.location.search.includes('?id')
      && !window.location.search.includes('?lat')
      && !window.location.search.includes('?eventid')
      && isLiteralNumber(latLS)
      && isLiteralNumber(lngLS)
      && isLiteralNumber(zoomLS)
    ) {
      setSearchParams({
        lat: latLS,
        lng: lngLS,
        zoom: zoomLS
      })
    }

    if (
      !window.location.search.includes('?id')
      && !window.location.search.includes('?eventid')
      && mapRef?.current
    ) {
      if (
        (
          lat !== null
          || lng !== null
          || zoom !== null
        )
        && (
          !isLiteralNumber(lat)
          || !isLiteralNumber(lng)
          || !isLiteralNumber(zoom)
        )
      ) {
        dispatch(
          getNotificationMessage({
            type: REJECTED,
            message: 'Invalid URL parameters',
            isEditProfile: true
          })
        )
      }

      if (
        lat === null
        && lng === null
      ) {
        let coord = []

        if (
          activeProfileLat
          && activeProfileLng
        ) {
          coord = [
            +activeProfileLat,
            +activeProfileLng
          ]
        } else if (
          user?.id
          || (
            localStorage.getItem('userLatLS')
            && localStorage.getItem('userLngLS')
          )
        ) {
          coord = centerCoordinates

          if (
            coord[0] === 'null'
            || coord[1] === 'null'
          ) {
            coord = INITIAL_POSITION
          }
        } else {
          coord = INITIAL_POSITION
        }

        if (
          !sessionStorage.getItem('id')
          && isAuth()
        ) {
          mapRef.current.setView(coord, ZOOM_LEVEL)
        }
      } else if (
        isLiteralNumber(latLS)
        && isLiteralNumber(lngLS)
        && isLiteralNumber(zoomLS)
      ) {
        mapRef.current.setView([
          +latLS,
          +lngLS
        ], zoomLS)
      } else if (
        isLiteralNumber(lat)
        && isLiteralNumber(lng)
        && isLiteralNumber(zoom)
      ) {
        mapRef.current.setView([
          +lat,
          +lng
        ], zoom)
      } else {
        mapRef.current.setView(centerCoordinates, ZOOM_LEVEL)
      }
    }
  }

  const autoSaveTiles = () => {
    const mapRefCurrent = mapRef?.current

    if (
      mapRefCurrent
      && mapRefCurrent?.getZoom() >= 4
    ) {
      const tlOffline = tileLayerOffline(
        MAP_URL,
        {
          attribution: MAP_ATTRIBUTION,
          maxNativeZoom: UNSUPPORTED_MAX_ZOOM_LEVEL,
          maxZoom: UNSUPPORTED_MAX_ZOOM_LEVEL,
          minNativeZoom: 4,
          minZoom: 4
        }
      ).addTo(mapRefCurrent)

      tlOffline.on('tileload', (e) => {
        const { tile } = e
        const url = tile.src
        if (url.search('blob:') !== -1) {
          return
        }
        const { x, y, z } = e.coords
        const { _url: urlTemplate } = e.target
        const tileInfo = {
          key: url,
          url,
          x,
          y,
          z,
          urlTemplate,
          createdAt: Date.now()
        }
        downloadTile(url)
          .then((dl) => saveTile(tileInfo, dl))
          .then(() => (process.env.REACT_APP_MAP_DEBUG === 'true' ? console.log(`Saved ${url} in idb`) : null))
      })

      // workaround
      let saveControl = savetiles(
        tlOffline,
        {
          alwaysDownload: true,
          confirm: (layer, successCallback) => {
            successCallback()
          },
          confirmRemoval: (layer, successCallback) => {
            successCallback()
          },
          bounds: mapRefCurrent?.getBounds(),
          saveText: '<i id="saveTiles" />',
          rmText: '<i id="deleteTiles" />'
        }
      )

      if (tlOffline) {
        saveControl.addTo(mapRefCurrent)
        document.querySelector('.savetiles.leaflet-bar').style.display = 'none'

        setLeafletOfflineInitialized(true)
      }
    }
  }

  const handleClosePopup = () => {
    if (mapRef.current) {
      const mapCenter = mapRef.current.getCenter()
      const clickedZoom = mapRef.current.getZoom()

      localStorage.setItem('lat', mapCenter.lat.toFixed(6))
      localStorage.setItem('lng', mapCenter.lng.toFixed(6))
      localStorage.setItem('zoom', clickedZoom)

      setSearchParams({
        lat: mapCenter.lat.toFixed(6),
        lng: mapCenter.lng.toFixed(6),
        zoom: clickedZoom
      })

      sessionStorage.removeItem('eventid')
      sessionStorage.removeItem('id')
      dispatch(setSelectedMarkerData(null))
      dispatch(setSelectedEventData(null))
      dispatch(addNaturalWidths([]))
      setPopupComponent('')
      setTimeout(() => {
        dispatch(setMarkerUpdate(true))
        dispatch(setEventMarkerUpdate(true))
        dispatch(setProfileItemId(''))
        dispatch(setEventId(null))
        dispatch(deleteProfileItem())
      }, 850)
      showCounter()
    }
  }

  const handleMarkerCloseOnDrag = () => {
    if (sessionStorage.getItem('id') !== null || sessionStorage.getItem('eventid') !== null) {
      mapRef?.current?.off('dragend', handleMarkerCloseOnDrag)

      handleClosePopup()
    }
  }

  const HandlePopupEvents = () => {
    useMapEvent('popupclose', () => {
      mapRef?.current?.off('dragend', handleMarkerCloseOnDrag)

      handleClosePopup()
    })

    useMapEvent('dragstart', () => {
      sessionStorage.removeItem('openProfileAfterAnimation')
      sessionStorage.removeItem('openEventAfterAnimation')
    })

    return null
  }

  useEffect(() => {
    if (
      (
        !openPopupBlogs
        && window.location.search.includes('?post-id')
      )
      || (
        !openPopupEvents
        && window.location.search.includes('?eventid')
      )
    ) {
      handleCoordinates()
    }
  }, [openPopupBlogs, openPopupEvents])

  useEffect(() => {
    if (
      selectedMarkerData
      && !selectedEventData
    ) {
      const markerPopupCoords = selectedMarkerData?.id === activeProfile?.id
      && !Number.isNaN(xmppLiveLocation.lat)
      && selectedMarkerData.location_type[LOCATION_TYPE.LiveLocation] === 0
        ? liveLocCoord
        : selectedMarkerData?.coordinates

      mapRef.current.setView(
        markerPopupCoords,
        mapRef.current.getZoom(),
        { animate: true }
      )

      dispatch(setAddFriendStatus('idle'))
      dispatch(setDeleteFriendStatus('idle'))
      dispatch(setRemoveFriendRequestStatus('idle'))
      dispatch(setPosts([]))

      setTimeout(() => {
        hideCounter()
        setTimeout(() => setSearchParams({ id: selectedMarkerData?.id }), 0)

        setPopupComponent(
          <MarkerPopup
            selectedMarkerData={selectedMarkerData}
            markerPopupCoords={markerPopupCoords}
            handleClosePopup={handleClosePopup}
          />
        )

        mapRef.current.on('dragend', handleMarkerCloseOnDrag)
      }, 500)
    } else if (
      selectedEventData
      && !selectedMarkerData
    ) {
      const eventMarkerPopupCoords = [selectedEventData?.lat, selectedEventData?.lng]

      mapRef.current.setView(
        eventMarkerPopupCoords,
        mapRef.current.getZoom(),
        { animate: true }
      )

      setTimeout(() => {
        hideCounter()
        setTimeout(() => setSearchParams({ eventid: selectedEventData.id }), 0)

        setPopupComponent(
          <EventMarkerPopup
            selectedEventData={selectedEventData}
            eventMarkerPopupCoords={eventMarkerPopupCoords}
            handleClosePopup={handleClosePopup}
          />
        )
        mapRef.current.on('dragend', handleMarkerCloseOnDrag)
      }, 500)
    }
  }, [selectedMarkerData?.id, selectedEventData?.id])

  // Kindly comment this useEffect if something goes wrong
  useEffect(() => {
    // Maybe the current leaflet offline doesn't support the use of WorldCopyJump?
    // This app's map is using WorldCopyJump which results to
    // the map bounds getting incorrect longitude on
    // initial load on or near the intl dateline
    // longitude can only have values < 181 and > -181

    const mapRefCurrent = mapRef?.current
    // eslint-disable-next-line no-underscore-dangle
    const boundsNE = mapRefCurrent?.getBounds()?._northEast
    // eslint-disable-next-line no-underscore-dangle
    const boundsSW = mapRefCurrent?.getBounds()?._southWest

    if (
      mapRefCurrent
      && !leafletOfflineInitialized
      && boundsNE?.lng < 181
      && boundsNE?.lng > -181
      && boundsSW?.lng < 181
      && boundsSW?.lng > -181
    ) {
      autoSaveTiles()
    }
  }, [mapRef?.current?.getBounds()])

  useEffect(() => {
    if (getMarkersStatus === SUCCEEDED) {
      handleVisibleMarkerCount()
    }
  }, [getMarkersStatus])

  useEffect(() => {
    if (getUserProfileStatus === SUCCEEDED) {
      handleVisibleMarkerCount()
    }
  }, [getUserProfileStatus])

  useEffect(() => {
    if (mapRef?.current) {
      const map = mapRef.current
      map.on('moveend', handleMapMoveEnd)

      map.on('popupopen', () => {
        handleVisibleMarkerCount(0)
        map.scrollWheelZoom.disable()
        map.touchZoom.disable()
        map.doubleClickZoom.disable()
        map.boxZoom.disable()
        map.keyboard.disable()
      })
      map.on('popupclose', () => {
        handleVisibleMarkerCount(0)
        map.scrollWheelZoom.enable()
        map.touchZoom.enable()
        map.doubleClickZoom.enable()
        map.boxZoom.enable()
        map.keyboard.enable()
      })

      handleInitialCenter()
    }
  }, [mapRef?.current])

  useEffect(() => {
    if (signInStatus === SUCCEEDED && (user?.id)) {
      handleInitialCenter()
    }
  }, [signInStatus, user?.id])

  // useEffect(() => {
  //   if (isEventMarkerClose === true) {
  //     console.log('clear popup working')
  //     setPopupComponent('')
  //     dispatch(closeEventMarker(false))
  //   }
  // }, [isEventMarkerClose])

  useEffect(() => {
    if (
      isLiteralNumber(lat)
      && isLiteralNumber(lng)
      && isLiteralNumber(zoom)
    ) {
      localStorage.setItem('lat', lat)
      localStorage.setItem('lng', lng)
      localStorage.setItem('zoom', zoom)
    }
  }, [])

  const northBound = Leaflet.latLng(85, Infinity)
  const southBound = Leaflet.latLng(-85, -Infinity)
  const fullBound = Leaflet.latLngBounds(northBound, southBound)

  // when clicking the notification button while the map is moving,
  // it will stop the current movement
  // don't add conditions in this. Add it on the usage of stopMap
  // useEffect(() => {
  //   mapRef?.current?.stop()
  // }, [mapStop])

  // temporarily disable the maxbounds once the marker popup opens
  // it is used for markers that are positioned on the northside
  // where the popup exceeds the northbound
  // disabled until further notice
  // useEffect(() => {
  //   if (popupComponent && mapRef) {
  //     mapRef?.current?.setMaxBounds(null)
  //   } else {
  //     mapRef?.current?.setMaxBounds(fullBound)
  //   }
  // }, [popupComponent])

  return (
    <StyledMapContainer>
      <MapContainer
        style={{ width: '100%', height: '100%' }}
        center={INITIAL_POSITION}
        zoom={ZOOM_LEVEL}
        minZoom={4}
        ref={mapRef}
        worldCopyJump
        maxBoundsViscosity={1.0}
        maxBounds={fullBound}
        attributionControl={false}
        whenReady={(e) => {
          setTimeout(() => {
            localStorage.setItem('northeast', JSON.stringify(e.target.getBounds().getNorthEast()))
            localStorage.setItem('southwest', JSON.stringify(e.target.getBounds().getSouthWest()))
          }, 1000)
        }}
      >
        <TileLayer
          attribution={MAP_ATTRIBUTION}
          url={MAP_URL}
          maxNativeZoom={22}
          maxZoom={UNSUPPORTED_MAX_ZOOM_LEVEL}
          eventHandlers={{
            // when tile loading fails (this does not include invalid keys)
            // tileerror: () => {
            //   dispatch(getNotificationMessage({
            //     type: REJECTED,
            //     message: 'Map tile error.',
            //     isEditProfile: false
            //   }))
            // }
          }}
        />
        <MarkerClusterGroup
          showCoverageOnHover={false}
          animate
          chunkedLoading
          className="marker-cluster-custom"
          spiderfyOnMaxZoom={false}
          disableClusteringAtZoom={14}
          maxClusterRadius={20}
          eventHandlers={{
            animationend: () => {
              if (window.location.search.includes('?id') || window.location.search.includes('?eventid')) {
                const popUpExists = document.querySelector('.leaflet-popup')

                if (!popUpExists) {
                  const mapCenter = mapRef.current.getCenter()
                  const clickedZoom = mapRef.current.getZoom()

                  localStorage.setItem('lat', mapCenter.lat.toFixed(6))
                  localStorage.setItem('lng', mapCenter.lng.toFixed(6))
                  localStorage.setItem('zoom', clickedZoom)

                  setSearchParams({
                    lat: mapCenter.lat.toFixed(6),
                    lng: mapCenter.lng.toFixed(6),
                    zoom: clickedZoom
                  })

                  sessionStorage.removeItem('id')
                  sessionStorage.removeItem('eventid')

                  setTimeout(() => {
                    setShowMarkerCount('flex')
                  }, 500)
                }
              }
            }
          }}
        >
          <Markers
            markers={markers}
            eventMarkers={eventMarkers}
            mapRef={mapRef}
          />
        </MarkerClusterGroup>
        { popupComponent }
        <HandlePopupEvents />
      </MapContainer>
    </StyledMapContainer>
  )
}
